import { useCallback, useEffect, useState } from 'react'
import { dismissReminder, getToday, setTaskDone } from '../lib/notes.js'
import { shortDate } from '../lib/format.js'
import ReminderSheet from '../components/ReminderSheet.jsx'

function dueLabel(due, today) {
  if (!due) return null
  if (due === today) return { text: 'today', cls: 'soon' }
  const days = Math.round((new Date(today) - new Date(due)) / 86400000)
  return { text: 'overdue ' + days + 'd', cls: 'late' }
}

/**
 * What is scheduled today, plus anything needing attention — including
 * something scheduled last week for today. Overdue rises to the top, because
 * a list that hides what slipped is the reason things slip.
 */
export default function Today({ categories, toast, refreshKey, onOpenInbox }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [openReminder, setOpenReminder] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await getToday())
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load, refreshKey])

  const catById = Object.fromEntries(categories.map((c) => [c.id, c]))

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read today. {error}</p>
      </div>
    )
  }
  if (!data) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  const { tasks, reminders, inboxCount, today } = data
  const nothing = tasks.length === 0 && reminders.length === 0 && inboxCount === 0

  return (
    <>
      <div className="page-head">
        <span className="lbl">{shortDate(today)}</span>
        <h1 className="serif">Today</h1>
      </div>

      {inboxCount > 0 ? (
        <button type="button" className="alert alert-btn" onClick={onOpenInbox}>
          <span className="lbl">Needs you · {inboxCount}</span>
          <p>
            {inboxCount === 1 ? 'One capture' : inboxCount + ' captures'} not filed yet.
            <em> File them →</em>
          </p>
        </button>
      ) : null}

      {nothing ? (
        <div className="empty">
          <h2 className="serif">Nothing due</h2>
          <p>Anything you capture or schedule shows up here on its day.</p>
        </div>
      ) : null}

      {tasks.length ? (
        <ul className="todo">
          {tasks.map((t) => {
            const cat = catById[t.category_id]
            const d = dueLabel(t.due_on, today)
            return (
              /* Same opt-in the reminder rows use, so the swipe and the
                 right-click menu both find the tick that is already here.
                 Nothing new is invented for either one to call. */
              <li key={t.id} data-context-row="">
                <button
                  type="button"
                  className="tick"
                  aria-label={'Mark done: ' + t.body}
                  data-context-action="Done"
                  onClick={async () => {
                    try {
                      await setTaskDone(t, true)
                      load()
                    } catch (e) {
                      toast(e.message || 'Could not update that.')
                    }
                  }}
                >
                  ✓
                </button>
                <i style={{ background: cat?.color || 'var(--ink-faint)' }} />
                <span>{t.body}</span>
                {d ? <em className={d.cls}>{d.text}</em> : null}
              </li>
            )
          })}
        </ul>
      ) : null}

      {reminders.length ? (
        <>
          <p className="lbl" style={{ margin: '20px 2px 8px' }}>Reminders</p>
          <div className="card">
            <div className="rows">
              {reminders.map((r) => (
                <div className="row" key={r.id} data-context-row="">
                  {/* The tick closes it outright, the same as on the Reminders
                      tab. Everything else about the reminder lives one tap in:
                      a reminder you cannot act on from where you are reading it
                      is just a label. */}
                  <button
                    type="button"
                    className="tick"
                    aria-label={'Dismiss: ' + r.body}
                    data-context-action="Dismiss"
                    onClick={async () => {
                      try {
                        await dismissReminder(r.id)
                        load()
                      } catch (e) {
                        toast(e.message || 'Could not dismiss that.')
                      }
                    }}
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className="rowopen"
                    onClick={() => setOpenReminder(r)}
                    aria-label={'Open reminder: ' + r.body}
                  >
                    <span className="nm">
                      <b>{r.body}</b>
                      <span className="lbl">
                        {new Date(r.remind_at).toLocaleTimeString('en-PH', {
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    </span>
                    <span className="go" aria-hidden="true">›</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {openReminder ? (
        <ReminderSheet
          reminder={openReminder}
          onClose={() => setOpenReminder(null)}
          onChanged={load}
          toast={toast}
        />
      ) : null}
    </>
  )
}
